import {
  FlightResultsQueryDataSchema,
  FlightSearchFormData,
  formDataToQueryParams,
} from '@reward-platform/flights-schemas'
import { parseISO } from 'date-fns'
import { useQueryState } from 'nuqs'
import { useCallback, useMemo } from 'react'
import useGetAirport from '~/hooks/useGetAirport/useGetAirport'

export function useFlightSearchFormData() {
  const [departureDateParam, setDepartureDate] = useQueryState('departureDate')
  const [returnDateParam, setReturnDate] = useQueryState('returnDate')
  const [fromAirportCode, setFromAirportCode] = useQueryState('fromAirportCode')
  const [toAirportCode, setToAirportCode] = useQueryState('toAirportCode')
  const [oneWayOnlyParam, setOneWayOnly] = useQueryState('oneWayOnly')
  const [adults, setAdults] = useQueryState('adult')
  const [children, setChildren] = useQueryState('child')
  const [childrenAges, setChildrenAges] = useQueryState('childrenAges')
  const [outboundJourneyFilter, setOutboundJourney] = useQueryState('outboundJourney')
  const [inboundJourneyFilter, setInboundJourney] = useQueryState('inboundJourney')
  const [outboundCabinFilter, setOutboundCabin] = useQueryState('outboundCabin')
  const [inboundCabinFilter, setInboundCabin] = useQueryState('inboundCabin')

  const fromAirportData = useGetAirport(fromAirportCode ?? undefined)
  const toAirportData = useGetAirport(toAirportCode ?? undefined)

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const formData = useMemo<Partial<FlightSearchFormData>>(() => {
    const input = {
      fromAirportCode: fromAirportCode ?? undefined,
      toAirportCode: toAirportCode ?? undefined,
      oneWayOnly: oneWayOnlyParam,
      adult: adults ?? '1',
      child: children ?? '0',
      childrenAges: childrenAges ?? undefined,
      departureDate: departureDateParam ?? undefined,
      returnDate: returnDateParam ?? undefined,
      outboundJourney: outboundJourneyFilter ?? undefined,
      inboundJourney: inboundJourneyFilter ?? undefined,
      outboundCabin: outboundCabinFilter ?? undefined,
      inboundCabin: inboundCabinFilter ?? undefined,
    }
    const queryParams = FlightResultsQueryDataSchema.partial().safeParse(input)

    if (!queryParams.success) {
      return {}
    }

    const oneWayOnly = queryParams?.data.oneWayOnly
    const departureDate = queryParams?.data.departureDate
    const returnDate = queryParams?.data.returnDate

    const adultsCount = queryParams?.data.adult
    const childrenCount = queryParams?.data.child
    const childrenAgesList = queryParams?.data.childrenAges

    return {
      toLocation: toAirportData,
      fromLocation: fromAirportData,
      oneWayOnly,
      dates: departureDate
        ? {
            startDateTime: parseISO(departureDate),
            endDateTime: !oneWayOnly && returnDate ? parseISO(returnDate) : null,
          }
        : undefined,
      travellers: {
        adult: { quantity: adults ? Number(adultsCount) : 1 },
        child: { quantity: children ? Number(children) : 0 },
        childrenAges:
          childrenCount && childrenAgesList
            ? childrenAgesList
                .split(',')
                .map((value, index) => ({ child: String(index + 1), age: Number(value) }))
            : [],
      },
    }
  }, [
    fromAirportCode,
    toAirportCode,
    oneWayOnlyParam,
    adults,
    children,
    childrenAges,
    departureDateParam,
    returnDateParam,
    toAirportData,
    fromAirportData,
    outboundJourneyFilter,
    inboundJourneyFilter,
    outboundCabinFilter,
    inboundCabinFilter,
  ])

  const setFlightSearchParams = useCallback(
    (data: FlightSearchFormData) => {
      const queryData = formDataToQueryParams(data)
      setFromAirportCode(queryData.fromAirportCode ?? null)
      setToAirportCode(queryData.toAirportCode ?? null)
      setOneWayOnly(String(queryData.oneWayOnly))
      setAdults(queryData.adult ?? null)
      setChildren(queryData.child ?? null)
      setChildrenAges(queryData.childrenAges ?? null)
      setDepartureDate(queryData.departureDate ?? null)
      setReturnDate(queryData.returnDate ?? null)
      setOutboundJourney(queryData.outboundJourney ?? null)
      setOutboundCabin(queryData.outboundCabin ?? null)
      setInboundJourney(queryData.inboundJourney ?? null)
      setInboundCabin(queryData.inboundCabin ?? null)
    },
    [
      setAdults,
      setChildren,
      setChildrenAges,
      setDepartureDate,
      setFromAirportCode,
      setOneWayOnly,
      setReturnDate,
      setToAirportCode,
      setOutboundJourney,
      setOutboundCabin,
      setInboundJourney,
      setInboundCabin,
    ]
  )
  return useMemo(() => ({ setFlightSearchParams, formData }), [setFlightSearchParams, formData])
}
